import React, { forwardRef } from 'react';
import Type from 'prop-types';
import { noop, capitalize } from 'lodash';
import cn from 'classnames';
import { Divider, InputBase, InputAdornment, IconButton } from "@mui/material";
import { Clear as ClearIcon, ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { useTranslation } from 'hooks';
import { t as translateFn } from 'utils/i18n';
import useSearchBar from './useSearchBar';
import styles from './SearchBar.module.css';

const SearchBar = forwardRef(function SearchBar(props, ref) {
  const { value, inputRef, onChange, onClear, onSearch } = useSearchBar(props);
  const {
    position,
    value: inputValue,
    fullWidth,
    visible,
    icon: Icon,
    onIconClick,
    inputProps,
    className,
    showDivider,
    disableGutters,
    blurOnSubmit,
    timeoutMs,
    onClear: onInputClear,
    onChange: onInputChange,
    onSearch: onInputSearch,
    ...other
  } = props;

  const { t } = useTranslation();
  const positionClassName = `position${capitalize(position)}`;

  const handleSubmit = (event) => {
    onSearch(event);
    if (blurOnSubmit && inputRef.current) {
      inputRef.current.blur();
    }
  };

  const renderStartAdornment = () => {
    return (
      Icon && (
        <InputAdornment position="start">
          <IconButton edge="start" onClick={onIconClick} data-testid="closeSearchBtn">
            <Icon />
          </IconButton>
        </InputAdornment>
      )
    );
  };

  const renderEndAdornment = () => {
    return (
      value && (
        <InputAdornment position="end">
          <IconButton edge="end" onClick={onClear}>
            <ClearIcon />
          </IconButton>
        </InputAdornment>
      )
    );
  };

  return (
    <>
      <form
        ref={ref}
        className={cn(className, {
          [styles.root]: true,
          [styles[positionClassName]]: true,
          [styles.fullWidth]: fullWidth,
          [styles.gutters]: !disableGutters,
        })}
        role="search"
        autoComplete="off"
        onSubmit={handleSubmit}
        style={{
          visibility: visible ? 'visible' : 'hidden',
        }}
      >
        <InputBase
          type="search"
          sx={{ '& input::-webkit-search-cancel-button': { display: 'none' } }}
          value={value}
          autoComplete="off"
          onChange={onChange}
          inputRef={inputRef}
          fullWidth={fullWidth}
          startAdornment={renderStartAdornment()}
          endAdornment={renderEndAdornment()}
          inputProps={{
            role: 'combobox',
            'aria-label': t('search'),
            'aria-haspopup': 'false',
            ...inputProps,
          }}
          {...other}
        />
      </form>
      {showDivider && <Divider />}
    </>
  );
});

SearchBar.propTypes = {
  position: Type.oneOf(['static', 'absolute']),
  placeholder: Type.string,
  fullWidth: Type.bool,
  showDivider: Type.bool,
  visible: Type.bool,
  disableGutters: Type.bool,
  blurOnSubmit: Type.bool,
  className: Type.string,
  timeoutMs: Type.number,
  icon: Type.elementType,
  inputProps: Type.object, // eslint-disable-line react/forbid-prop-types
  value: Type.string,
  onChange: Type.func,
  onClear: Type.func,
  onSearch: Type.func,
  onIconClick: Type.func,
};

SearchBar.defaultProps = {
  position: 'absolute',
  value: '',
  placeholder: translateFn('search'),
  fullWidth: false,
  showDivider: false,
  disableGutters: false,
  blurOnSubmit: false,
  visible: true,
  timeoutMs: 1500,
  className: null,
  inputProps: {},
  icon: ArrowBackIcon,
  onChange: noop,
  onClear: noop,
  onIconClick: noop,
  onSearch: noop,
};

export default SearchBar;
